import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SlidersHorizontal } from 'lucide-react';
import { CodeArtifact, CodeProject, CodeSession } from './types';
import { ArtifactCardRouter } from './ArtifactCardRouter';
import { ArtifactFilterSheet, ArtifactFilters } from './ArtifactFilterSheet';
import { EmptyState } from '../EmptyState';
interface Props {
  isOpen: boolean;
  onClose: () => void;
  projects: CodeProject[];
  sessions: CodeSession[];
  artifacts: CodeArtifact[];
  onAnswerHitl: (artifactId: string, answer: string) => void;
  onViewSession: (sessionId: string) => void;
}
export function AllArtifactsSheet({
  isOpen,
  onClose,
  projects,
  sessions,
  artifacts,
  onAnswerHitl,
  onViewSession
}: Props) {
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<ArtifactFilters>({
    targetIds: new Set(),
    kinds: new Set()
  });
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !filterOpen) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, filterOpen, onClose]);
  const breadcrumbFor = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of artifacts) {
      const session = sessions.find(s => s.id === a.sessionId);
      if (!session) {
        map.set(a.id, '—');
        continue;
      }
      const target = projects.flatMap(p => p.targets).find(t => t.id === session.targetId);
      const project = projects.find(p => p.id === target?.projectId);
      map.set(a.id, `${project?.name ?? ''} · ${target?.name ?? ''} · ${session.title}`);
    }
    return map;
  }, [artifacts, sessions, projects]);
  const filtered = useMemo(() => {
    const anyTarget = filters.targetIds.size === 0;
    const anyKind = filters.kinds.size === 0;
    const list = artifacts.filter(a => {
      const session = sessions.find(s => s.id === a.sessionId);
      if (!session) return false;
      const targetOk = anyTarget || filters.targetIds.has(session.targetId);
      const kindOk = anyKind || filters.kinds.has(a.kind);
      return targetOk && kindOk;
    });
    return list.sort((a, b) => {
      const aP = a.kind === 'hitl-step' && a.status === 'pending' ? 0 : 1;
      const bP = b.kind === 'hitl-step' && b.status === 'pending' ? 0 : 1;
      return aP - bP;
    });
  }, [artifacts, sessions, filters]);
  const activeFilterCount = filters.targetIds.size + filters.kinds.size;
  return <>
      <AnimatePresence data-id="element-400">
        {isOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="fixed inset-0 bg-black/60 z-[70]" onClick={onClose} data-id="element-401" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 32,
          stiffness: 280
        }} drag="y" dragConstraints={{
          top: 0,
          bottom: 0
        }} dragElastic={0.2} onDragEnd={(_, info) => {
          if (info.offset.y > 120) onClose();
        }} className="fixed inset-x-0 bottom-0 z-[71] bg-[#141418] rounded-t-[20px] border-t border-white/[0.06] flex flex-col" style={{
          height: '90vh'
        }} data-id="element-402">
              <div className="flex justify-center pt-2 pb-1" data-id="element-403">
                <div className="w-10 h-1 rounded-full bg-[#8a8680]/30" data-id="element-404" />
              </div>
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.04]" data-id="element-405">
                <div className="text-[15px] text-[#e8e4df]" data-id="element-406">All artifacts</div>
                <button onClick={() => setFilterOpen(true)} className="relative w-9 h-9 flex items-center justify-center rounded-full bg-white/[0.04] text-[#8a8680] hover:text-[#e8e4df] hover:bg-white/[0.06] transition-colors" data-id="element-407">
                  <SlidersHorizontal size={14} strokeWidth={1.8} data-id="element-408" />
                  {activeFilterCount > 0 && <div className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[#4a9a9a]" data-id="element-409" />}
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4" data-id="element-410">
                {filtered.length === 0 ? <EmptyState message="No artifacts match these filters." data-id="element-411" /> : filtered.map(artifact => <ArtifactCardRouter key={artifact.id} artifact={artifact} breadcrumb={breadcrumbFor.get(artifact.id)} onAnswerHitl={onAnswerHitl} onViewInSession={() => onViewSession(artifact.sessionId)} data-id="element-412" />)}
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      <ArtifactFilterSheet isOpen={filterOpen} onClose={() => setFilterOpen(false)} projects={projects} filters={filters} onApply={setFilters} data-id="element-413" />
    </>;
}